import { yupResolver } from '@hookform/resolvers/yup';
import { ExpandMore } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
	Stack,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
	CheckboxButtonGroup,
	PasswordElement,
	TextFieldElement,
} from 'react-hook-form-mui';
import * as yup from 'yup';
import {
	ExternalServiceIntegration,
	ExternalServiceIntegrationAddRequest,
	ExternalServiceIntegrationUpdateRequest,
	ExternalServiceSecuritySpec,
} from '../../../api/integrations';
import {
	ACTION_CANCEL,
	ACTION_CREATE,
	ACTION_SAVE,
	EDIT_LABEL,
	HEADER_VALUE_LABEL,
	INTEGRATION_HEADER_LABEL,
	INTEGRATION_HMAC_ENABLED_SECRET_MISSING_ERROR,
	INTEGRATION_NAME_EXISTS_FIELD_ERROR,
	INTEGRATION_NAME_FORMAT_FIELD_ERROR,
	INTEGRATION_NAME_LABEL,
	INTEGRATION_REQUIRED_FIELD_ERROR,
	INTEGRATION_SECRET_FORMAT_FIELD_ERROR,
	INTEGRATION_SECURITY_SPEC_HEADER_LABEL,
	INTEGRATION_SECURITY_SPEC_HMAC_ENABLED_LABEL,
	INTEGRATION_SECURITY_SPEC_INFO_MSG,
	INTEGRATION_SECURITY_SPEC_SECRET_LABEL,
	INTEGRATION_SECURITY_SPEC_SECURE_SSL_LABEL,
	INTEGRATION_SECURITY_SPEC_TAG_LABEL,
	INTEGRATION_TAG_FORMAT_FIELD_ERROR,
	INTEGRATION_TAG_PRESENT_SECRET_MISSING_ERROR,
	INTEGRATION_URL_FIELD_ERROR,
	INTEGRATION_URL_LABEL,
	NEW_LABEL,
	UNEDITABLE_FIELD_INFO,
} from '../../../data/constants';
import { convertHeadersToRequest } from '../../../utils';
import { handleModalClose } from '../../../utils/handleModalClose';

interface Props {
	open: boolean;
	isLoading: boolean;
	integrationNames: string[];
	integrationType: () => string;
	integrationTypeLabel: () => string;
	onCancel: () => void;
	onAdd: (
		integrationAddRequest: ExternalServiceIntegrationAddRequest,
	) => void | Promise<void>;
	onEdit: (
		integrationUpdateRequest: ExternalServiceIntegrationUpdateRequest,
		integrationName: string,
	) => void;
	integration?: ExternalServiceIntegration;
}

interface Header {
	headerName: string;
	value: string;
}

interface SecuritySpec {
	secret?: string;
	tag?: string;
	secureSSL: string[];
	hmacEnabled: string[];
}

interface IntegrationForm {
	type: string;
	name: string;
	url: string;
	headers: Header[];
	securitySpec: SecuritySpec;
}

const SECURITY_SPEC_HMAC_ENABLED = 'hmacEnabled';
const SECURITY_SPEC_SECURE_SSL = 'secureSSL';

const schema = (existingNames: string[]) =>
	yup.object({
		name: yup
			.string()
			.required(INTEGRATION_REQUIRED_FIELD_ERROR())
			.matches(/^[a-zA-Z0-9-_.]+$/, INTEGRATION_NAME_FORMAT_FIELD_ERROR())
			.test(
				'is-unique',
				INTEGRATION_NAME_EXISTS_FIELD_ERROR(),
				(value) => !existingNames.includes(value),
			),
		url: yup
			.string()
			.required(INTEGRATION_REQUIRED_FIELD_ERROR())
			.url(INTEGRATION_URL_FIELD_ERROR()),
		headers: yup.array().of(
			yup.object({
				headerName: yup
					.string()
					.trim()
					.required(INTEGRATION_REQUIRED_FIELD_ERROR()),
			}),
		),
		securitySpec: yup.object({
			secret: yup
				.string()
				.trim()
				.optional()
				.when('hmacEnabled', {
					is: (value: string[]) => value.length === 1,
					then: yup
						.string()
						.required(INTEGRATION_HMAC_ENABLED_SECRET_MISSING_ERROR),
				})
				.when('tag', {
					is: (value: string) => !!value,
					then: yup
						.string()
						.required(INTEGRATION_TAG_PRESENT_SECRET_MISSING_ERROR),
				})
				.test(
					'secret-pattern',
					INTEGRATION_SECRET_FORMAT_FIELD_ERROR(),
					(value) => {
						if (!value) {
							return true;
						}
						return !!value.match(/^[a-zA-Z0-9_]{32,64}$/);
					},
				),
			tag: yup
				.string()
				.trim()
				.optional()
				.test('tag-pattern', INTEGRATION_TAG_FORMAT_FIELD_ERROR(), (value) => {
					if (!value) {
						return true;
					}
					return !!value.match(/^[a-zA-Z0-9_]{2,32}$/);
				}),
		}),
	});

const isHttps = (url: string) => url.startsWith('https://');

export default function AddUpdateIntegrationModal(props: Props) {
	const {
		open,
		isLoading,
		integration,
		integrationNames,
		integrationType,
		integrationTypeLabel,
		onCancel,
		onAdd,
		onEdit,
	} = props;

	const isEditing = !!integration;

	const [expandSecuritySpec, setExpandSecuritySpec] = useState<boolean>(false);
	const [hasHmacEnabledBeenChanged, setHasHmacEnabledBeenChanged] =
		useState<boolean>(!isEditing);

	const { handleSubmit, control, watch, formState, setValue, getFieldState } =
		useForm<IntegrationForm>({
			mode: 'onChange',
			resolver: yupResolver(schema(isEditing ? [] : integrationNames)),
			defaultValues: {
				type: integrationType(),
				name: integration ? integration.name : '',
				url: integration ? integration.url : '',
				headers: integration
					? Object.keys(integration.headers).map((key) => ({
							headerName: key,
							value: integration.headers[key],
					  }))
					: [],
				securitySpec:
					integration && integration.securitySpec
						? {
								secret: integration?.securitySpec?.secret,
								tag: integration?.securitySpec?.tag,
								hmacEnabled: integration?.securitySpec?.hmacEnabled
									? [SECURITY_SPEC_HMAC_ENABLED]
									: [],
								secureSSL:
									integration?.securitySpec?.secureSSL === false
										? []
										: [SECURITY_SPEC_SECURE_SSL],
						  }
						: {
								hmacEnabled: [],
								secureSSL: [SECURITY_SPEC_SECURE_SSL], //https has by default secureSSL enabled
						  },
			},
		});
	const currentUrl = watch('url');
	const currentSecret = watch('securitySpec.secret');

	const { fields, remove, append } = useFieldArray({
		control,
		name: 'headers',
	});

	useEffect(() => {
		const isSecretPresentAndValid =
			!getFieldState('securitySpec.secret', formState).invalid &&
			!!currentSecret &&
			currentSecret.length > 10; //this not the actual validation; is just to prevent setting the hmacEnabled when first character is added to the secret
		if (
			isSecretPresentAndValid &&
			hasHmacEnabledBeenChanged &&
			isHttps(currentUrl)
		) {
			setValue('securitySpec.hmacEnabled', [SECURITY_SPEC_HMAC_ENABLED]);
		}
	}, [
		formState,
		currentSecret,
		setValue,
		hasHmacEnabledBeenChanged,
		currentUrl,
	]);

	useEffect(() => {
		if (!isHttps(currentUrl) && expandSecuritySpec) {
			setExpandSecuritySpec(false);
		} else if (
			!!formState.errors?.securitySpec &&
			isHttps(currentUrl) &&
			!expandSecuritySpec
		) {
			setExpandSecuritySpec(true);
		}
	}, [formState, currentUrl, expandSecuritySpec]);

	useEffect(() => {
		if (!isEditing && !!currentSecret && !isHttps(currentUrl)) {
			setValue('securitySpec.secret', undefined);
		}
	}, [setValue, currentSecret, isEditing, currentUrl]);

	const convertToSecuritySpecRequest = (
		values: IntegrationForm,
	): ExternalServiceSecuritySpec | null => {
		if (!isHttps(values.url)) {
			return null;
		}
		let hmacEnabled =
			values?.securitySpec.hmacEnabled[0] === SECURITY_SPEC_HMAC_ENABLED;
		if (
			typeof integration?.securitySpec?.hmacEnabled === 'undefined' &&
			!hasHmacEnabledBeenChanged
		) {
			hmacEnabled = null;
		}
		return {
			secret: values?.securitySpec?.secret,
			tag: values?.securitySpec?.tag,
			secureSSL: values?.securitySpec.secureSSL[0] === SECURITY_SPEC_SECURE_SSL,
			hmacEnabled: hmacEnabled,
		};
	};

	const onSubmit: SubmitHandler<IntegrationForm> = (values) => {
		if (isEditing) {
			const request: ExternalServiceIntegrationUpdateRequest = {
				url: values.url,
				...(values.headers.length && {
					headers: convertHeadersToRequest(values.headers),
				}),
				securitySpec: convertToSecuritySpecRequest(values),
			};
			onEdit(request, values.name);
		} else {
			const request: ExternalServiceIntegrationAddRequest = {
				type: integrationType(),
				name: values.name,
				url: values.url,
				...(values.headers.length && {
					headers: convertHeadersToRequest(values.headers),
				}),
				securitySpec: convertToSecuritySpecRequest(values),
			};
			onAdd(request);
		}
	};

	return (
		<>
			<Dialog
				fullWidth
				open={open}
				onClose={(e, r) => handleModalClose(r, onCancel)}
				aria-labelledby='add-integration-modal'
			>
				<Box component='form' onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle>
						{!isEditing
							? NEW_LABEL(integrationTypeLabel)
							: EDIT_LABEL(integrationTypeLabel)}
					</DialogTitle>
					<DialogContent>
						<Stack
							alignItems='flex-start'
							component='form'
							direction='column'
							spacing={2}
						>
							<TextFieldElement
								name='name'
								autoFocus={!isEditing}
								fullWidth
								label={INTEGRATION_NAME_LABEL()}
								variant='outlined'
								sx={{ backgroundColor: 'white.lightest', mt: 1 }}
								helperText={UNEDITABLE_FIELD_INFO()}
								disabled={isEditing || isLoading}
								control={control}
							/>
							<TextFieldElement
								name='url'
								autoFocus={isEditing}
								fullWidth
								label={INTEGRATION_URL_LABEL()}
								variant='outlined'
								sx={{ backgroundColor: 'white.lightest' }}
								disabled={isLoading}
								control={control}
							/>
							<Box width={1}>
								<Accordion
									disabled={!isHttps(currentUrl)}
									expanded={expandSecuritySpec}
									onChange={() => {
										if (!formState.errors?.securitySpec) {
											setExpandSecuritySpec(!expandSecuritySpec);
										}
									}}
									sx={{
										width: '100%',
										borderRadius: '4px',
										border: '1px solid rgba(0, 0, 0, 0.25)',
										boxShadow: 'none',
									}}
								>
									<AccordionSummary expandIcon={<ExpandMore />}>
										<Typography>
											{INTEGRATION_SECURITY_SPEC_HEADER_LABEL()}
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Stack spacing={2}>
											<PasswordElement
												autoFocus
												label={INTEGRATION_SECURITY_SPEC_SECRET_LABEL()}
												name='securitySpec.secret'
												control={control}
												disabled={isLoading}
												variant='outlined'
												size='small'
												fullWidth
												autoComplete='off'
											/>
											<TextFieldElement
												autoFocus
												label={INTEGRATION_SECURITY_SPEC_TAG_LABEL()}
												name='securitySpec.tag'
												control={control}
												disabled={isLoading}
												variant='outlined'
												size='small'
												fullWidth
												autoComplete='off'
											/>
											<Stack direction='row' spacing={4}>
												<CheckboxButtonGroup
													onChange={() => setHasHmacEnabledBeenChanged(false)}
													name='securitySpec.hmacEnabled'
													control={control}
													options={[
														{
															id: SECURITY_SPEC_HMAC_ENABLED,
															label:
																INTEGRATION_SECURITY_SPEC_HMAC_ENABLED_LABEL(),
														},
													]}
													row
													checkboxColor='secondary'
												/>
												<CheckboxButtonGroup
													name='securitySpec.secureSSL'
													control={control}
													options={[
														{
															id: SECURITY_SPEC_SECURE_SSL,
															label:
																INTEGRATION_SECURITY_SPEC_SECURE_SSL_LABEL(),
														},
													]}
													row
													checkboxColor='secondary'
												/>
											</Stack>

											<Link
												sx={{
													display: 'flex',
													flexDirection: 'row',
													alignContent: 'center',
													textDecoration: 'none',
													color: '#777777',
												}}
												target='_blank'
												href='https://docs.socotra.com/production/api/integrations.html#integrations-api'
											>
												<InfoOutlinedIcon
													fontSize='small'
													sx={{ marginRight: '5px' }}
												/>
												<Typography variant='body2' align='left'>
													{INTEGRATION_SECURITY_SPEC_INFO_MSG()}
												</Typography>
											</Link>
										</Stack>
									</AccordionDetails>
								</Accordion>
							</Box>
							<Typography>{INTEGRATION_HEADER_LABEL(true)}</Typography>
							{fields.map((field, index) => (
								<Stack
									spacing={2}
									direction='row'
									key={field.id}
									sx={{ width: 1 }}
								>
									<TextFieldElement
										autoFocus
										label={INTEGRATION_NAME_LABEL()}
										name={`headers.${index}.headerName`}
										control={control}
										disabled={isLoading}
										variant='outlined'
										size='small'
										fullWidth
									/>
									<TextFieldElement
										label={HEADER_VALUE_LABEL()}
										name={`headers.${index}.value`}
										control={control}
										disabled={isLoading}
										variant='outlined'
										size='small'
										fullWidth
									/>
									<IconButton
										disabled={isLoading}
										onClick={() => remove(index)}
									>
										<CloseIcon />
									</IconButton>
								</Stack>
							))}
							<Button
								startIcon={<AddIcon />}
								variant='text'
								disabled={isLoading}
								onClick={() => append({ headerName: '', value: '' })}
							>
								{NEW_LABEL(INTEGRATION_HEADER_LABEL)}
							</Button>
						</Stack>
					</DialogContent>
					<DialogActions sx={{ m: 2 }}>
						<Button onClick={onCancel} color='neutralButton'>
							{ACTION_CANCEL()}
						</Button>
						<Button color='secondary' variant='contained' type='submit'>
							{!isEditing ? ACTION_CREATE() : ACTION_SAVE()}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>
		</>
	);
}
